
import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { _eventBus } from "../../../core/events/EventBus";
import { isEmpty } from "../../../Lib/Utils/validationUtil";
import { evtTree } from "../../../Orchestrator/ConstantTree";
import { useRecState } from "../../../Orchestrator/Recoils/Events/Recoil";
import {
  ILocationPopupProps,
  IUserResponse,
  SearchStateType,
  StationsListType,
  StationType,
  SubStation,
} from "../../../Orchestrator/Types/Index";
import List from "./List";
import dynamic from "next/dynamic";
import Image from "next/image";
import { fmlmSearchTree } from "../../../Orchestrator/ConstantTree/FmlmSearch/FmlmSearchTree";
import { LocationPermission } from "../../../Orchestrator/Types/Types/FmlmSearch/FmlmSearchType";

// mapbox-gl is ~200KB gz; load it only when the map popup is actually opened
// instead of bundling it into the landing/search chunk.
const LocationMapPopup = dynamic(() => import("./FmlmSearch/LocationMapPopup"), {
  ssr: false,
});

const LocationPopup: React.FC<ILocationPopupProps> = ({
  visible,
  onSelect,
  locations,
  locationFor,
  isFromPopUp
}) => {
  const intl = useIntl();
  const [isMapVisible] = useRecState<boolean>("locationMapVisibleAtom");
  const [openDropdowns] = useRecState<{ [key: string]: boolean }>("subStationDropdownAtom");
  const [userData] = useRecState<IUserResponse>("userDataAtom");
  const [initialized] = useRecState<boolean>("initializedStateAtom");
  const [isAnonymous] = useRecState<boolean>("isAnonymousStateAtom");
  const [search] = useRecState<SearchStateType>("searchStateAtom");
  const [searchPopupData] = useRecState<SearchStateType>("searchStatePopupAtom");
  const [locationPermission, setLocationPermission] = useRecState<LocationPermission>(
    "locationPermissionStateAtom"
  );
  const [isSelectingOrigin] = useRecState<boolean>("isSelectingOriginAtom");

  let selectedLocation = useMemo(() => {
    let searchState = isFromPopUp ? searchPopupData : search;
    if (locationFor === "from") return searchState?.to;
    else if (locationFor === "to") return searchState?.from;
  }, [searchPopupData, search, locationFor]);

  let selectedLocationName = useMemo(() => {
    let searchState = isFromPopUp ? searchPopupData : search;
    if (locationFor === "from") return searchState?.toName;
    else if (locationFor === "to") return searchState?.fromName;
  }, [searchPopupData, search, locationFor]);

  const toggleFavorite = async (id: string, e: React.MouseEvent) => {
    const initAuth = async () => {
      let isLogging: boolean = true;
      try {
        // Check if the user is logging out
        const isLoggingOut = sessionStorage.getItem("isLoggingOut") === "true";

        if (!isLoggingOut && isAnonymous) {
          // Store /search path for redirect after login
          sessionStorage.setItem("redirectAfterLogin", "/search");
          // Use KeyCloakService directly for login
          //await KeyCloakService.login();
          isLogging = false;
        }
      } catch (error) {
        console.error("Authentication error:", error);
        isLogging = false;
      } finally {
        sessionStorage.removeItem("isLoggingOut");
        return isLogging;
      }
    };
    if (initialized) {
      //Check if anyone try to star location without login
      if (await initAuth()) {
        _eventBus.emit({
          eventName: evtTree.SearchBar.Favourite.Click,
          data: {
            userData: userData,
            locations,
            value: id,
            locationFor: locationFor
          },
        });
      }
    }
  };
  const toggleDropdown = (name: string) => {
    // setOpenDropdowns((prev) => ({ ...prev, [name]: !prev[name] }));
    _eventBus.emit({
      eventName: evtTree.SearchBar.SubStations.Click,
      data: { dropdownName: name, openDropdowns: openDropdowns },
    });
  };

  const favoriteStopsId = useMemo((): string[] => {
    const originKey = locationFor === "from" ? "origin" : "destination";
    return userData?.railPreferences?.favoriteDepartures?.filter((stop) => originKey in stop).map((i) => i[originKey]).filter((id): id is string => typeof id === 'string') || [];
  }, [userData, locationFor, locations])

  const filteredLocations = (stopLocation: any) => {
    let filteredLocations = [...stopLocation];

    if (favoriteStopsId && favoriteStopsId.length) {
      const favoriteLocations = filteredLocations.filter((loc) => favoriteStopsId.includes(loc.id)).map(loc => ({ ...loc, subStations: [] }));
      const favoriteSubStations = filteredLocations.flatMap((d: any) => d.subStations).filter((sub: any) => favoriteStopsId.includes(sub.id) && sub.type === 'bus');

      const filteredStopList: StationsListType = (filteredLocations as StationsListType).map((loc) => {
        let newLocation = { ...loc }

        if (loc.subStations.length > 0) {
          newLocation.subStations = loc?.subStations?.filter((sub: SubStation) => !favoriteStopsId.includes(sub.id || "")) || [];
        }

        return newLocation;
      }).filter(loc => !favoriteStopsId.includes(loc.id));

      let favoritesStops: any[] = []
      favoritesStops = [...favoritesStops, ...favoriteLocations.map(loc => ({ ...loc, subStations: [] })), ...favoriteSubStations.map(i => ({ ...i, subStations: [] }))];

      const locationMap = new Map(favoriteStopsId.map((k, i) => [k, i]));
      favoritesStops.sort((current, next) => {
        return (locationMap.get(current.id) ?? Infinity) - (locationMap.get(next.id) ?? Infinity);
      });

      filteredLocations = [...favoritesStops, ...filteredStopList];
    }

    // Filter out shuttle stations - show only train stations as a flat list
    filteredLocations = filteredLocations
      .filter((loc: any) => loc.type !== "bus")
      .map((loc: any) => ({
        ...loc,
        subStations: [],
      }));

    // NOTE: we intentionally do NOT filter out the station already selected in
    // the other field — all stations are shown in both "From" and "To".
    // If the user picks the station that is currently set in the other field,
    // that other field is cleared in SelectOriginDestinationAction.

    return filteredLocations
  };

  // Locally-computed station list that <List/> renders directly. Computing it here
  // (instead of waiting for the Stops.Updated event to round-trip through a flow
  // step into updatedStopsAtom) makes the list appear instantly when the dropdown
  // opens — that round-trip, debounced 200ms below and gated on a flow subscriber
  // being ready, was the first-open lag. The atom is identical to this value
  // (the flow just stores what we emit), so other consumers stay consistent.
  const displayLocations = useMemo(() => {
    if (!locations || locations.length === 0) return [];
    return filteredLocations([...locations]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, locationFor, selectedLocation, selectedLocationName, favoriteStopsId]);

  // Keep updatedStopsAtom in sync for other consumers (FMLM map, JourneyPlanner).
  // List no longer depends on this, so the debounce here is harmless.
  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout;
    if (locations.length > 0 && visible) {
      const data: any = filteredLocations([...locations]);
      debounceTimeout = setTimeout(() => {
        _eventBus.emit({
          eventName: evtTree.SearchBar.Stops.Updated,
          data: { data: [...data] },
        });
      }, 200);
    }
    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
    };
  }, [locations, locationFor, visible, selectedLocation, selectedLocationName, favoriteStopsId]);

  const handleMapOpen = async () => {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported");
      const newPermission = { isGranted: false };
      setLocationPermission(newPermission);
      _eventBus.emit({
        eventName: evtTree.SearchBar.LocationMap.Click,
        data: { visible: true, locationPermission: newPermission },
      });
      return;
    }

    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPermission = { isGranted: true, position };
          setLocationPermission(newPermission);
          _eventBus.emit({
            eventName: evtTree.SearchBar.LocationMap.Click,
            data: { visible: true, locationPermission: newPermission },
          });
        },
        (error) => {
          console.warn("Geolocation error:", error.message);
          const newPermission = { isGranted: false };
          setLocationPermission(newPermission);
          _eventBus.emit({
            eventName: evtTree.SearchBar.LocationMap.Click,
            data: { visible: true, locationPermission: newPermission },
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } catch (err) {
      console.error("Error requesting geolocation:", err);
      const newPermission = { isGranted: false };
      setLocationPermission(newPermission);
      _eventBus.emit({
        eventName: evtTree.SearchBar.LocationMap.Click,
        data: { visible: true, locationPermission: newPermission },
      });
    }
  };

  const handleMapClose = () => {
    _eventBus.emit({
      eventName: fmlmSearchTree.close.Click,
      data: { visible: false, locationFor: isSelectingOrigin ? "from" : "to", search: search },
    });
  };

  if (isMapVisible && visible) {
    return (
      <LocationMapPopup
        popupFor="FMLMSearch"
        isVisible={isMapVisible}
        onClose={handleMapClose}
        locationFor={locationFor}
        selectedExactLocation={isSelectingOrigin ? "origin" : "destination"}
        locations={locations}
        locationPermission={locationPermission}
        isFromPopUp={isFromPopUp}
        onSelect={(id: string, name: string, isShuttle?: boolean, geoPosition?: any) => {
          _eventBus.emit({
            eventName: fmlmSearchTree.selectStops.Select,
            data: {
              field: isSelectingOrigin ? "from" : "to",
              id: id,
              search: search,
              name: name,
              fmlmSearch: false,
              isShuttle: isShuttle,
              geoPosition: geoPosition,
            },
          });
        }}
      />
    );
  }

  if (!visible) return null;

  return (
    <>
      <div className="location-popup">
        <List
          locations={displayLocations}
          favorites={favoriteStopsId}
          onSelect={onSelect}
          onToggleFavorite={toggleFavorite}
          onToggleDropdown={toggleDropdown}
          openDropdowns={openDropdowns}
          locationFor={locationFor}
          isFromPopUp={isFromPopUp}
        />
        {/* "Enter other location" button hidden for now */}
        {false && (
          <div className="enter-location" data-id="enter-exact-location" onClick={handleMapOpen}>
            <div>
              <Image src="/search/exact-location.svg" width={20} height={20} alt="Location Icon" />
              <span className="fs-15px fw-500 color-2F3033">
                {intl.formatMessage({ id: "enterExactLocation" })}
              </span>
            </div>
            <div className="mr-10">
              <Image
                src="/search/right-icon.svg"
                width={24}
                height={24}
                alt="icon"
                className="mr-5"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LocationPopup;
